import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useAppearance } from "../contexts/AppearanceContext";
import { BarChart2, TrendingUp, GitBranch, CheckSquare, Target, ArrowDownCircle, Users, MessageCircle, Activity, Clock, Layers, LayoutDashboard } from "lucide-react";
import { useReportFilters } from "./reports/useReportFilters";
import ReportFilterBar from "./reports/ReportFilterBar";
import VendasReport from "./reports/VendasReport";
import PerformanceReport from "./reports/PerformanceReport";
import ForecastReport from "./reports/ForecastReport";
import PipelineReport from "./reports/PipelineReport";
import TarefasReport from "./reports/TarefasReport";
import ConversaoReport from "./reports/ConversaoReport";
import WhatsAppReport from "./reports/WhatsAppReport";
import AtividadesReport from "./reports/AtividadesReport";
import AgingReport from "./reports/AgingReport";
import CohortReport from "./reports/CohortReport";
import ExecutiveReport from "./reports/ExecutiveReport";

const REPORT_ITEMS = {
  vendas:    { key: "vendas",    labelKey: "relatorios.desempenho",  descKey: "relatorios.desempenhoDesc",  icon: TrendingUp },
  forecast:  { key: "forecast",  labelKey: "relatorios.forecast",    descKey: "relatorios.forecastDesc",    icon: Target },
  conversao: { key: "conversao", labelKey: "relatorios.conversao",   descKey: "relatorios.conversaoDesc",   icon: ArrowDownCircle },
  pipeline:  { key: "pipeline",  labelKey: "relatorios.pipeline",    descKey: "relatorios.pipelineDesc",    icon: GitBranch },
  tarefas:   { key: "tarefas",   labelKey: "relatorios.tarefasTitle",descKey: "relatorios.tarefasDesc",     icon: CheckSquare },
  whatsapp:  { key: "whatsapp",  labelKey: "WhatsApp",               descKey: "whatsapp.reportDesc",        icon: MessageCircle },
  atividades:{ key: "atividades",labelKey: "Atividades",             descKey: "atividades.reportDesc",      icon: Activity },
  aging:     { key: "aging",     labelKey: "Estagnados",              descKey: "aging.reportDesc",           icon: Clock },
  cohort:    { key: "cohort",    labelKey: "Cohort",                  descKey: "cohort.reportDesc",          icon: Layers },
  executive: { key: "executive", labelKey: "Executivo",               descKey: "executive.reportDesc",       icon: LayoutDashboard },
};

const REPORT_MENU = [
  {
    group: "Vendas",
    items: [
      { key: "executive", labelKey: "Executivo",                icon: LayoutDashboard, raw: true },
      { key: "vendas",    labelKey: "relatorios.desempenho", icon: TrendingUp },
      { key: "forecast",  labelKey: "relatorios.forecast",   icon: Target },
    ],
  },
  {
    group: "Pipeline",
    items: [
      { key: "conversao", labelKey: "relatorios.conversao",  icon: ArrowDownCircle },
      { key: "pipeline",  labelKey: "relatorios.pipeline",   icon: GitBranch },
      { key: "aging",     labelKey: "Estagnados",            icon: Clock, raw: true },
      { key: "cohort",    labelKey: "Cohort",                icon: Layers, raw: true },
    ],
  },
  {
    group: "Tarefas",
    items: [
      { key: "tarefas",   labelKey: "relatorios.tarefasTitle", icon: CheckSquare },
    ],
  },
  {
    group: "Integrações",
    items: [
      { key: "whatsapp",  labelKey: "WhatsApp", icon: MessageCircle },
    ],
  },
  {
    group: "Engajamento",
    items: [
      { key: "atividades", labelKey: "Atividades", icon: Activity, raw: true },
    ],
  },
];

export default function RelatoriosPage({ reportKey = "vendas" }) {
  const { t } = useTranslation();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  const filterHook = useReportFilters(reportKey);

  const activeItem = REPORT_ITEMS[reportKey];

  const renderReport = () => {
    switch (reportKey) {
      case "performance": return null; // merged into vendas
      case "vendas":    return <VendasReport filterHook={filterHook} />;
      case "forecast":  return <ForecastReport filterHook={filterHook} />;
      case "pipeline":  return <PipelineReport filterHook={filterHook} />;
      case "conversao":  return <ConversaoReport filterHook={filterHook} />;
      case "tarefas":   return <TarefasReport filterHook={filterHook} />;
      case "whatsapp":  return <WhatsAppReport filterHook={filterHook} />;
      case "atividades": return <AtividadesReport filterHook={filterHook} />;
      case "aging":      return <AgingReport filterHook={filterHook} />;
      case "cohort":     return <CohortReport filterHook={filterHook} />;
      case "executive":  return <ExecutiveReport filterHook={filterHook} />;
      default:          return null;
    }
  };

  return (
    <div data-testid="relatorios-page" className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        {activeItem && (
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: appearance.colors?.["50"] }}>
            <activeItem.icon className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
          </div>
        )}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{activeItem ? (["whatsapp","atividades","aging","cohort","executive"].includes(activeItem.key) ? activeItem.labelKey : t(activeItem.labelKey)) : ""}</h1>
          {activeItem?.descKey && <p className="text-slate-500 mt-0.5 text-sm">{
            activeItem.key === "whatsapp" ? "Métricas e análise das conversas via WhatsApp" :
            activeItem.key === "atividades" ? "Engajamento e produtividade dos usuários no sistema" :
            activeItem.key === "aging" ? "Negócios parados há muito tempo em cada etapa do pipeline" :
            activeItem.key === "cohort" ? "Análise de conversão dos negócios criados em cada mês" :
            activeItem.key === "executive" ? "Visão consolidada de todos os indicadores num único lugar" :
            t(activeItem.descKey)
          }</p>}
        </div>
      </div>

      {/* Filter bar */}
      <ReportFilterBar hook={filterHook} />

      {/* Report content */}
      {renderReport()}
    </div>
  );
}
